import { useEffect, useRef, useState } from "react";
import { Modal } from "antd";
import { Button1 } from "../index";
import { securityApi } from "../../api/endpoints";

const BRAND_GREEN = "#1BBB6B";

const ERROR_MESSAGES = {
  OTP_REQUIRED: "Please enter the 6-digit code we emailed you.",
  OTP_INVALID: "That code is incorrect. Please try again.",
  OTP_EXPIRED: "That code has expired. Request a new one.",
  OTP_MAX_ATTEMPTS: "Too many wrong attempts. Request a new code.",
  OTP_RESEND_TOO_SOON: "Please wait a moment before requesting another code.",
};

const PinInput = ({ label, value, onChange }) => (
  <div className="d-flex flex-column gap-2">
    <label className="fs12px">{label}</label>
    <input
      type="password"
      inputMode="numeric"
      maxLength={6}
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))}
      placeholder="••••••"
      className="form-control text-center fw-bold"
      style={{ fontSize: 22, letterSpacing: 10, padding: "10px 8px", borderColor: BRAND_GREEN }}
    />
  </div>
);

/**
 * Set or change the 6-digit transaction PIN. Requires a freshly emailed OTP
 * as identity proof (same email channel used for transfer verification),
 * so this can't be done from a hijacked session without inbox access.
 */
const TransactionPinModal = ({ open, onClose, hasPin, onSaved }) => {
  const [otp, setOtp] = useState("");
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const requestedRef = useRef(false);

  const requestCode = async () => {
    setError(null);
    try {
      const res = await securityApi.requestOtp();
      setInfo("We've sent a 6-digit code to your email.");
      setCooldown(res.resendAfterSeconds || 60);
    } catch (err) {
      const apiCode = err.response?.data?.error;
      if (apiCode === "OTP_RESEND_TOO_SOON") {
        setInfo("A code was already sent, check your email.");
        setCooldown(30);
      } else {
        setError(ERROR_MESSAGES[apiCode] || "Could not send the code. Try again.");
      }
    }
  };

  useEffect(() => {
    if (open && !requestedRef.current) {
      requestedRef.current = true;
      requestCode();
    }
    if (!open) {
      requestedRef.current = false;
      setOtp("");
      setPin("");
      setConfirmPin("");
      setError(null);
      setInfo(null);
      setCooldown(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleSave = async () => {
    if (submitting) return;
    if (!/^\d{6}$/.test(otp)) {
      setError("Please enter the 6-digit code we emailed you.");
      return;
    }
    if (!/^\d{6}$/.test(pin)) {
      setError("Your PIN must be exactly 6 digits.");
      return;
    }
    if (pin !== confirmPin) {
      setError("PIN and confirmation do not match.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await securityApi.setPin(otp, pin);
      onSaved?.();
      onClose?.();
    } catch (err) {
      const apiCode = err.response?.data?.error;
      setError(ERROR_MESSAGES[apiCode] || apiCode || err.message || "Could not save PIN");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onCancel={onClose} footer={null} width={440} closable>
      <div className="d-flex flex-column gap-3 p-2">
        <span className="fs-5 fw-semibold">{hasPin ? "Change your transaction PIN" : "Set a transaction PIN"}</span>
        <span className="fs12px" style={{ color: "#7D7D7D" }}>
          {info || "Sending your verification code..."}
        </span>

        <div className="d-flex flex-column gap-2">
          <label className="fs12px">Verification code</label>
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
            placeholder="______"
            className="form-control text-center fw-bold"
            style={{ fontSize: 22, letterSpacing: 10, padding: "10px 8px", borderColor: BRAND_GREEN }}
          />
        </div>

        <PinInput label={hasPin ? "New 6-digit PIN" : "Choose a 6-digit PIN"} value={pin} onChange={setPin} />
        <PinInput label="Confirm PIN" value={confirmPin} onChange={setConfirmPin} />

        {error && (
          <span className="fs12px" style={{ color: "#FC555B" }}>{error}</span>
        )}

        <div className="d-flex align-items-center justify-content-between">
          <span
            className={`fs12px ${cooldown > 0 ? "" : "cursor-pointer"}`}
            style={{ color: cooldown > 0 ? "#7D7D7D" : BRAND_GREEN }}
            onClick={() => cooldown <= 0 && requestCode()}
          >
            {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
          </span>
          <Button1
            buttonName={submitting ? "Saving..." : "Save PIN"}
            className="btn-dark"
            onClick={handleSave}
            disabled={submitting || otp.length !== 6 || pin.length !== 6 || confirmPin.length !== 6}
          />
        </div>
      </div>
    </Modal>
  );
};

export default TransactionPinModal;
